import { ComponentType, FC, ReactNode, createContext, useContext, useMemo } from 'react';

import { ToolbarActionService } from '../services/ToolbarActionService';

const ToolbarActionServiceContext = createContext<ToolbarActionService | undefined>(undefined);

export { ToolbarActionServiceContext };

export function ToolbarActionServiceProvider({ children }: { children: ReactNode }) {
  const service = useMemo(() => new ToolbarActionService(), []);
  return (
    <ToolbarActionServiceContext.Provider value={service}>
      {children}
    </ToolbarActionServiceContext.Provider>
  );
}

export function useToolbarActionService(): ToolbarActionService {
  const context = useContext(ToolbarActionServiceContext);
  if (!context) {
    throw new Error('useToolbarActionService must be used within ToolbarActionServiceProvider');
  }
  return context;
}

interface WithToolbarActionServiceProps {
  readonly toolbarActionService: ToolbarActionService;
}

/**
 * HOC that injects the `ToolbarActionService` as a `toolbarActionService` prop.
 *
 * @deprecated Use the `useToolbarActionService` hook in functional components instead.
 */
export function withToolbarActionService<P extends WithToolbarActionServiceProps>(
  Component: ComponentType<P>,
): FC<Omit<P, keyof WithToolbarActionServiceProps>> {
  return function WithToolbarActionService(props: Omit<P, keyof WithToolbarActionServiceProps>) {
    const toolbarActionService = useToolbarActionService();
    return <Component {...(props as unknown as P)} toolbarActionService={toolbarActionService} />;
  };
}
